import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { styled, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useI18N } from "../../context/I18NContext";
import { useSettings } from "../../context/SettingsContext";
import useWidgetStore from "../../store/useWidgetStore";
import { usePoll } from "../../hooks/query/usePoll";
import { useForm } from "react-hook-form";
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    verticalAlign: 'top',
    textAlign: 'right',
    minWidth: '4em'
}));
export default function PollingPaymentStatus(props) {
    const { t } = useI18N();
    const theme = useTheme();
    const { payment } = useSettings();
    const [provider, setProvider] = useState({
        key: 'none',
        label: 'Niet afrekenen',
        icon: ''
    });
    const [isPolling, setIsPolling] = useState(false);
    const [orderId, setOrderId] = useState("");
    // Mirrors `orderId` for the poll interval below to read instead of the state itself. That
    // interval is only rebuilt when currentPoller/isPolling change (see its own effect's
    // dependency array further down) - a plain state read closed into it would go stale the moment
    // orderId changes for some other reason while already polling (e.g. the mandate 1-cent-payment
    // case below, which creates a follow-up order once the consumer confirms their IBAN). A ref
    // sidesteps that: unlike state, reading .current always returns whatever was set most recently,
    // regardless of which render's closure is doing the reading.
    const orderIdRef = useRef("");
    const [errorCount, setErrorCount] = useState(0);
    const [currentPoller, setCurrentPoller] = useState({});
    const formContext = useForm({});
    const pollQuery = usePoll(formContext);
    const options = {
        timeBeforeFirstPoll: 1,
        timeBetweenPolls: 1
    };
    const { cart, setCart } = useWidgetStore();
    useEffect(() => {
        payment.providers.map((provider) => {
            if (provider.key === props.method) {
                setProvider(provider);
            }
        });
    }, [props.method]);
    const startPayment = (e) => {
        // delay enabling of the polling, so the form has time to "post",
        // i.e. open the payment in a new tab
        // if we don't do this, the form will get removed from the DOM before the new
        // tab is opened, throwing this error:
        // "form submission canceled because the form is not connected"
        // The form is not connected because we just removed it from DOM
        if (props.method !== "none") {
            setTimeout(() => {
                const order_id = cart.order_id;
                if (order_id) {
                    formContext.setValue("orderId", order_id);
                    orderIdRef.current = order_id;
                    setOrderId(order_id);
                    startPolling(order_id);
                }
                else {
                    stopPolling();
                }
                // FFS... because my Yii dev is ridiculously slow for some reason... I started polling while the payments were still
                // being prepared... explaining all the randomness and hours and hours of nonsense spent on this
            }, 5000);
        }
    };
    useEffect(() => {
        startPayment(true);
    }, [props.paymentId]);
    useEffect(() => {
        var _a, _b, _c;
        if (pollQuery.data) {
            let response = pollQuery.data;
            let success = ((_a = response === null || response === void 0 ? void 0 : response.status) === null || _a === void 0 ? void 0 : _a.status) === 'success', failed = ((_b = response === null || response === void 0 ? void 0 : response.status) === null || _b === void 0 ? void 0 : _b.status) === 'failed', data = ((_c = response === null || response === void 0 ? void 0 : response.result) === null || _c === void 0 ? void 0 : _c.item) || {};
            //console.log('Poll ok: ', success, response);
            if (success) {
                //Trigger an event to push the data to the widget session
                if (data.payment) {
                    // the setCart replaces the event handler
                    if (cart.order_id) {
                        let payment = {
                            // ...cart.currentPayment ?? ,
                            status: data.payment.transaction.status,
                            transaction: data.payment.transaction
                        };
                        if (cart.order_id === orderId && orderId !== data.payment.order_id) {
                            // only update the orderId once, as that is the one that was generated in the new tab
                            // any additional new orderIds are  generated when the mandate payment  is processed ( the 1 cent payment)
                            // which creates a new order, or more like the original order with the appointment and membership as its
                            // payment method
                            // that order in turn will be finalized after the consumer has confirmed  their iban and name
                            orderIdRef.current = data.payment.order_id;
                            setOrderId(data.payment.order_id);
                        }
                        setCart(Object.assign(Object.assign({}, cart), { currentPayment: payment }));
                    }
                    let transaction = data.payment.transaction;
                    if (transaction === null || transaction === void 0 ? void 0 : transaction.final) {
                        stopPolling();
                    }
                }
                setCurrentPoller((previousPoller) => {
                    return Object.assign(Object.assign({}, previousPoller), { forcePoll: false, timeSinceLastPoll: 0, currentPoll: undefined });
                });
            }
            else if (failed) {
                // The /pollPayment call itself failed - the payment/transaction isn't linked in
                // our own database yet (a common, expected race right after opening Mollie; see
                // the "started polling while the payments were still being prepared" comment on
                // startPayment's own delay above), or some other transient backend/DB hiccup. This
                // says nothing about the actual Mollie transaction, so it must never be confused
                // with a real terminal status - Mollie's own "failed"/"cancelled"/"expired" only
                // ever arrive through the success branch above, as data.payment.transaction.status.
                // So this never stops polling and never touches cart.currentPayment - it just keeps
                // trying, however long that takes, until an explicit status comes back.
                setErrorCount(errorCount + 1);
                setCurrentPoller((previousPoller) => {
                    return Object.assign(Object.assign({}, previousPoller), { forcePoll: false, timeSinceLastPoll: 0, currentPoll: undefined });
                });
            }
        }
    }, [pollQuery.data]);
    // this gets run when the currentPoller has changed
    useEffect(() => {
        if (!currentPoller || !isPolling)
            return;
        currentPoller.id = window.setInterval(() => {
            setCurrentPoller((previousPoller) => {
                return Object.assign(Object.assign({}, previousPoller), { timeSinceStart: previousPoller.timeSinceStart + 1, timeSinceLastPoll: previousPoller.timeSinceLastPoll + 1 });
            });
            let pollPossible = document.visibilityState !== 'hidden'
                && !currentPoller.currentPoll;
            let pollReady = (currentPoller.timeSinceStart > options.timeBeforeFirstPoll)
                && (currentPoller.timeSinceLastPoll > options.timeBetweenPolls);
            if (pollPossible) { // && (pollReady || currentPoller.forcePoll)) {
                setCurrentPoller((previousPoller) => {
                    return Object.assign(Object.assign({}, previousPoller), { currentPoll: true });
                }); // curiously, using an IIFE on the next line requires a semi-colon
                // orderIdRef, not the orderId state - see the ref's own comment above for why
                formContext.setValue("orderId", orderIdRef.current);
                pollQuery.refetch();
            }
        }, 1000);
        return () => {
            // clean up the interval of this instance of the poller
            if (currentPoller.id) {
                window.clearInterval(currentPoller.id);
            }
        };
    }, [currentPoller, isPolling]);
    const startPolling = (order_id) => {
        stopPolling();
        //Initialize a polling function
        // The polling function is called every second, put it will not perform a poll every second
        //Polling is done:
        // - every x seconds
        // -  ..., but not if the window does not have focus
        // -  ..., but not in the first y seconds
        //Polling stops:
        // - if the payment has reached a final status
        let _currentPoller = {
            //  id: undefined, // id as returned by setInterval (used for stopping the polling)
            timeSinceStart: 0,
            timeSinceLastPoll: 0,
            forcePoll: false,
            //  currentPoll: null, //Used to prevent two polling requests at the same time
            paymentId: order_id
        };
        setCurrentPoller(_currentPoller);
        setIsPolling(true);
    };
    const stopPolling = () => {
        setIsPolling(false);
        if (!currentPoller || !currentPoller.id) {
            return;
        }
        window.clearInterval(currentPoller.id);
        setCurrentPoller((previousPoller) => {
            return Object.assign(Object.assign({}, previousPoller), { id: undefined });
        });
    };
    const forcePolling = () => {
        if (!currentPoller) {
            return;
        }
        setCurrentPoller(Object.assign(Object.assign({}, currentPoller), { forcePoll: true }));
    };
    return (_jsx(_Fragment, { children: props.method && (_jsxs(_Fragment, { children: [_jsx(TableContainer, { component: Paper, sx: {
                        marginBottom: 4
                    }, children: _jsx(Table, { sx: { width: '100%' }, size: "small", "aria-label": "a dense table", children: _jsx(TableBody, { children: _jsxs(TableRow, { sx: {
                                    backgroundColor: theme.palette.background.default,
                                    '&:last-child td, &:last-child th': { border: 0 }
                                }, children: [_jsx(TableCell, { component: "th", scope: "row", sx: {
                                            fontSize: '1rem'
                                        }, children: t(provider.label, "payment") }), _jsx(StyledTableCell, { children: provider.icon && (_jsx("img", { src: provider.icon, style: {
                                                height: '3em',
                                                aspectRatio: '4 / 3',
                                                boxSizing: 'border-box'
                                            } })) })] }) }) }) }), isPolling && (_jsx(Card, { children: _jsx(CardContent, { children: _jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { margin: 2, textAlign: 'center' }, children: t('De status van je betaling wordt automatisch gecontroleerd...') }), _jsx(LinearProgress, {})] }) }) })), !isPolling && props.method === "none" && (_jsx(Card, { children: _jsx(CardContent, { children: _jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { margin: 2, textAlign: 'center' }, children: t('Bezig met het verwerken van je bestellling...') }), _jsx(LinearProgress, {})] }) }) }))] })) }));
}
